import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MdAdd, MdEdit } from "react-icons/md";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import InputField from "components/fields/InputField";
import Select from "components/fields/Select";
import Button, { IconButton } from "components/button";
import Card from "components/card";
import Avatar from "components/avatar";
import AlertPane from "components/alert";
import { PageContainer, PageHeader } from "components/page";
import NewChildForm from "./new-child-form";

const GENDER_OPTIONS = [
  { value: "na", label: "Prefer not to tell" },
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Non-binary" },
];

const Registration = () => {
  const [step, setStep] = useState("parent-details");

  const [queryParams] = useSearchParams();
  const phone = queryParams.get("phone");

  const [user, setUser] = useState({ phone: phone });
  const [guardians, setGuardians] = useState([]);
  const [children, setChildren] = useState([]);
  const [errors, setErrors] = useState([]);
  const [saving, setSaving] = useState(false);

  const fieldState = (path) =>
    errors.some((error) => error.path === path) ? "error" : undefined;

  const register = (details) => {
    setErrors([]);
    const formData = new FormData();
    for (const key in details) {
      formData.append(key, details[key]);
    }
    return AtriumApi.post("/v1/users/register", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
      .then((resp) => {
        if (resp.status === 422) {
          setErrors(resp.data.errors);
          return;
        }
        if (step === "parent-details") {
          const nextStep = guardians.length > 0 ? "overview" : "kid-details";
          setGuardians([
            ...guardians.filter((x) => x.id !== resp.data.id),
            resp.data,
          ]);
          setUser({ related: phone, role: "child" });
          setStep(nextStep);
        } else if (step === "kid-details") {
          setChildren([
            ...children.filter((x) => x.id !== resp.data.id),
            resp.data,
          ]);
          setStep("overview");
        } else {
          throw new Error("unreachable");
        }
      })
      .catch((error) => {
        notify.error(getApiErrorMessage(error, "Could not save registration"));
      });
  };

  const onParentSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    register(user).finally(() => setSaving(false));
  };

  const descriptions = {
    "parent-details": guardians.length
      ? "Add a parent or guardian."
      : "Register to continue.",
    "kid-details": "Register to continue.",
    overview: "Check your registration details.",
  };

  const saveError = errors.length > 0 && (
    <AlertPane tone="danger" className="">
      We couldn’t save these details. Check the highlighted fields and try again.
    </AlertPane>
  );

  return (
    <PageContainer>
      <PageHeader
        title="Registration"
        description={descriptions[step]}
        backTo={`/member/check-in?phone=${phone}`}
        backLabel="Back to Check-in"
      />

      {step === "parent-details" && (
        <form onSubmit={onParentSubmit} noValidate={true} className="flex flex-col gap-6">
          {saveError}
          <section className="flex flex-col gap-4">
            <h2 className="text-heading">Parent Details</h2>
            <InputField
              label="First Name"
              placeholder="First name"
              value={user.firstName || ""}
              onChange={(e) => setUser({ ...user, firstName: e.target.value })}
              state={fieldState("firstName")}
            />
            <InputField
              label="Last Name"
              placeholder="Last name"
              value={user.lastName || ""}
              onChange={(e) => setUser({ ...user, lastName: e.target.value })}
              state={fieldState("lastName")}
            />
            <InputField
              label="Email"
              placeholder="Email"
              type="email"
              autoComplete="email"
              value={user.email || ""}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
              state={fieldState("email")}
            />
            <InputField
              label="Phone"
              placeholder="Phone"
              type="tel"
              autoComplete="tel"
              value={user.phone || ""}
              onChange={(e) => setUser({ ...user, phone: e.target.value })}
              state={fieldState("phone")}
            />
            <Select
              label="Gender"
              placeholder="Select gender"
              required
              value={user.gender || ""}
              onChange={(e) => setUser({ ...user, gender: e.target.value })}
              state={fieldState("gender")}
              options={GENDER_OPTIONS}
            />
          </section>

          <Button type="submit" size="lg" fullWidth loading={saving}>
            Continue
          </Button>
        </form>
      )}

      {step === "kid-details" && (
        <>
          {saveError}
          <NewChildForm onSubmit={(child) => register({ ...user, ...child })} errors={errors} />
        </>
      )}

      {step === "overview" && (
        <>
          <section className="flex flex-col gap-3">
            <h2 className="text-heading">Parents / Guardians</h2>
            <ul className="flex flex-col gap-2">
              {guardians.map((guardian) => (
                <li key={"guardian" + guardian.id}>
                  <Card padding="sm">
                    <div className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold">
                          {guardian.firstName} {guardian.lastName}
                        </p>
                        <p className="text-sm text-fg-muted">{guardian.phone}</p>
                      </div>
                      <IconButton
                        icon={<MdEdit className="h-5 w-5" />}
                        label={`Edit ${guardian.firstName} ${guardian.lastName}`}
                      />
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
            <div>
              <Button
                variant="secondary"
                leftIcon={<MdAdd aria-hidden="true" className="h-5 w-5" />}
                onClick={() => {
                  setUser({ related: phone, role: "guardian" });
                  setStep("parent-details");
                }}
              >
                Add Parent/Guardian
              </Button>
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-heading">Children</h2>
            <ul className="flex flex-col gap-2">
              {children.map((child, i) => (
                <li key={"child" + i}>
                  <Card padding="sm">
                    <div className="flex items-center gap-4">
                      <Avatar user={child} size="lg" />
                      <div className="min-w-0 flex-1">
                        <p className="font-bold">
                          {child.firstName} {child.lastName}
                        </p>
                        <p className="text-sm text-fg-muted">
                          {new Date().getFullYear() - new Date(child.dob).getFullYear()}{" "}
                          years
                        </p>
                      </div>
                      <IconButton
                        icon={<MdEdit className="h-5 w-5" />}
                        label={`Edit ${child.firstName} ${child.lastName}`}
                      />
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
            <div>
              <Button
                variant="secondary"
                leftIcon={<MdAdd aria-hidden="true" className="h-5 w-5" />}
                onClick={() => {
                  setUser({ related: phone, role: "child" });
                  setStep("kid-details");
                }}
              >
                Add Child
              </Button>
            </div>
          </section>

          <Button size="lg" fullWidth>
            Confirm and Register
          </Button>
        </>
      )}
    </PageContainer>
  );
};

export default Registration;
